import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChildren, QueryList, ElementRef } from "@angular/core";
import { Router } from "@angular/router";

import { ImageFlipperService } from "../image-flipper.service";
import { ProjectService } from "../../shared/services/project.service";
import { take, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { NzNotificationService } from "ng-zorro-antd/notification";

@Component({
    selector: "app-copilot-sidebar",
    templateUrl: "./copilot-sidebar.component.html",
    styleUrls: ["./copilot-sidebar.component.css"],
    standalone: false
})
export class CopilotSidebarComponent implements OnInit, OnDestroy {
  @Input() collapsed = false;
  @Output() collapseToggle = new EventEmitter<void>();

  @ViewChildren("editInput") editInputs: QueryList<ElementRef<HTMLInputElement>>;

  saved_convos: { [key: string]: any } = {};
  filterText = "";
  activeKey: string | null = null;
  editingKey: string | null = null;
  editTitleValue = "";
  private destroy$ = new Subject<void>();

  constructor(
    private _router: Router,
    private imageFlipperService: ImageFlipperService,
    private projectService: ProjectService,
    private notificationService: NzNotificationService
  ) {}

  ngOnInit() {
    // load saved conversations from backend
    this.loadSavedConversations();

    // listen for refresh events
    this.imageFlipperService.refresh_conversations$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (shouldRefresh) => {
          if (shouldRefresh) {
            this.loadSavedConversations();
          }
        }
      });

    // highlight the conversation currently being viewed
    this.imageFlipperService.saved_convo_key$
      .pipe(takeUntil(this.destroy$))
      .subscribe((key) => {
        this.activeKey = key;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filteredConvoNames(): string[] {
    const names = Object.keys(this.saved_convos);
    const filter = this.filterText.trim().toLowerCase();
    if (!filter) {
      return names;
    }
    return names.filter((name) => name.toLowerCase().includes(filter));
  }

  loadSavedConversations() {
    this.projectService.getSavedConversations()
      .pipe(take(1))
      .subscribe({
        next: (conversations) => {
          this.saved_convos = conversations;
        },
        error: (err) => {
          console.error('Failed to load conversations in sidebar:', err);
        }
      });
  }

  startNewChat() {
    // exit any saved-conversation view and ask the chat for a fresh thread
    this.imageFlipperService.update_saved_convo_key(null);
    this.imageFlipperService.trigger_new_chat();
    this._router.navigate(['/xwai/chat']);
  }

  selectConvo(convoName: string) {
    this.imageFlipperService.update_saved_convo_key(convoName);
  }

  startEditConvo(convoName: string, event: Event) {
    event.stopPropagation();
    this.editingKey = convoName;
    this.editTitleValue = convoName;
    // wait for the input to render before focusing it
    setTimeout(() => this.editInputs.first?.nativeElement.focus());
  }

  cancelEditConvo(event?: Event) {
    event?.stopPropagation();
    this.editingKey = null;
    this.editTitleValue = "";
  }

  confirmEditConvo(convoName: string, event?: Event) {
    event?.stopPropagation();
    const newTitle = this.editTitleValue.trim();
    if (!newTitle || newTitle === convoName) {
      this.cancelEditConvo();
      return;
    }
    this.projectService.renameConversation(convoName, newTitle)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.notificationService.success(
            'Success',
            `Conversation renamed to "${newTitle}"`
          );
          this.editingKey = null;
          this.editTitleValue = "";
          this.loadSavedConversations();
          // keep the renamed conversation highlighted if it was active
          if (this.activeKey === convoName) {
            this.imageFlipperService.update_saved_convo_key(newTitle);
          }
        },
        error: (err) => {
          this.notificationService.error(
            'Error',
            err.error?.error || 'Failed to rename conversation'
          );
        }
      });
  }

  deleteConvo(convoName: string) {
    this.projectService.deleteConversation(convoName)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.notificationService.success(
            'Success',
            `Conversation "${convoName}" has been deleted`
          );
          // remove from local list
          delete this.saved_convos[convoName];
          // reload conversations to ensure sync
          this.loadSavedConversations();
          // if the deleted conversation is currently active, reset to new conversation
          this.imageFlipperService.update_saved_convo_key(null);
        },
        error: (err) => {
          this.notificationService.error(
            'Error',
            err.error?.error || 'Failed to delete conversation'
          );
        }
      });
  }
}
